import {
    Paper,
    Skeleton
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { Box, useTheme } from "@mui/system";
import {
    useContext,
    useEffect,
    useMemo
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { normalizeLevel } from "src/components/competitive-intelligence";
import { CompetitiveIntelligenceContext } from "src/components/competitive-intelligence/components/CompetitiveIntelligenceProvider";
import DataGridPerformanceTableBrandOverview from "src/components/data-grid/data-grid-performance-split-brand-overview";
import { useGetFiltersQuery } from "src/context/api/businessOverview/api";
import { useGetPerformanceSplitQuery } from "src/context/api/brandOverview/api";
import { useGetCompetitiveIntelligenceKpisQuery } from "src/context/api/competitorsMonitoring/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { fDate, getDateRangeIso } from "src/utils/format-time";
import BankingWidgetSummary from "./banking-widget-summary";
import { useGetKpisQuery } from "src/context/api/trafficAnalysis/api";
import { setFilterCompare, setFilterDate, setFilterDateCompare, setFilterLoadedCountry, setFilterMinDatePicker, setFilterTimestep } from "src/context/reducers/filter-settings/filterSlice";
import React from "react";
import { subDays } from "date-fns";
import { useTranslation } from "react-i18next";

// 🔹 Sous-composant pour éviter les rerenders inutiles
const BankingStatsGrid = React.memo(function BankingStatsGrid({ stats, isFetching, kpisData }: any) {
    return (
        <Grid container spacing={2}>
            {stats.map((stat: any, index: any) => (
                <Grid key={index} xs={6} md={4} lg={3}>
                    {isFetching || !kpisData ? (
                        <Skeleton
                            width="100%"
                            height="138px"
                            style={{ borderRadius: 10 }}
                            variant="rounded"
                            animation="wave"
                        />
                    ) : (
                        <BankingWidgetSummary
                            spanColor={stat.spanColor}
                            title={stat.title}
                            compareData={stat.compareData}
                            textColor={stat.textColor}
                            icon={stat.icon}
                            color={stat.color}
                            percent={0}
                            total={stat.value}
                            chart={{ series: stat.series }}
                        />
                    )}
                </Grid>
            ))}
        </Grid>
    );
});

export default function BrandOverview() {
    const workspace: any = useCurrentWorkspace();
    const filterStore = useSelector((state: any) => state.filters);
    const context = useContext(CompetitiveIntelligenceContext);
    const theme = useTheme();

    const dispatch = useDispatch();
    const { t } = useTranslation();


    const ColorSchema = {
        PRIMARY: "primary",
        SECONDARY: "secondary",
        TERTIARY: "tertiary",
        ERROR: "error",
        WARNING: "warning",
        INFO: "info",
        SUCCESS: "success",
    } as const;

    const { data: kpisData, error: kpisError, isFetching: isFetchingKpis } = useGetKpisQuery(
        {
            startDate:
                filterStore.start !== undefined
                    ? fDate(filterStore.start, "yyyy-MM-dd")
                    : null,
            endDate:
                filterStore.end !== undefined
                    ? fDate(filterStore.end, "yyyy-MM-dd")
                    : null,
            compareStartDate: filterStore.compare
                ? filterStore.compareStart !== undefined
                    ? fDate(filterStore.compareStart, "yyyy-MM-dd")
                    : null
                : null,
            compareEndDate: filterStore.compare
                ? filterStore.compareEnd !== undefined
                    ? fDate(filterStore.compareEnd, "yyyy-MM-dd")
                    : null
                : null,
            workspaceId: workspace?.id,
            countries: filterStore.countries,
            devices: filterStore.devices,
            keywords: filterStore.keywords,
            platforms: filterStore.platforms,
            timestep: filterStore.timestep,
        },
        {
            skip: !filterStore.start || !filterStore.end || !workspace?.id,
            refetchOnMountOrArgChange: true,
        }
    );

    const calculateCompareData = (
        current: number | undefined,
        previous: number | undefined
    ): number => {
        if (!current || !previous) return 0;
        return ((current - previous) / previous) * 100;
    };

    const getRevenueOrConversionsLabel = (workspace: any) => {
        const isEcommerce = workspace?.template === "E-commerce";
        const isLead = workspace?.template === "Lead";

        const hasGA4 =
            Array.isArray(workspace?.conversions_google_analytics) &&
            workspace.conversions_google_analytics.length > 0;
        const hasAds =
            Array.isArray(workspace?.conversions_google_ads) &&
            workspace.conversions_google_ads.length > 0;

        const isCustom = workspace?.custom === true;

        if (isLead) {
            if (isCustom) return t("dashboard.global.custom_based_conversions");
            if (hasGA4) return t("dashboard.global.total_search_conversions"); // GA4 prioritaire
            if (hasAds) return t("dashboard.global.brand_conversions_gads");
        }

        if (isEcommerce) {
            if (isCustom) return t("dashboard.global.custom_based_revenue");
            if (hasGA4) return t("dashboard.global.total_search_revenue"); // GA4 prioritaire
            if (hasAds) return t("dashboard.global.brand_revenue_gads");
        }

        return "N/A";
    };

    // 🔹 bankinStats stabilisés avec useMemo
    const bankinStats = useMemo(() => {
        if (!kpisData) {
            // Retourne 4 placeholders pour forcer le rendu des Skeletons
            return Array(4).fill({ placeholder: true });
        }

        return [
            {
                title: t("dashboard.global.savings"),
                value:
                    filterStore?.savingsFormulaFilter === "m1"
                        ? kpisData?.savings_avg ?? 0
                        : kpisData?.savings_mom ?? 0,
                compareData: calculateCompareData(
                    filterStore?.savingsFormulaFilter === "m1"
                        ? kpisData?.savings_avg ?? 0
                        : kpisData?.savings_mom ?? 0,
                    filterStore?.savingsFormulaFilter === "m1"
                        ? kpisData?.savings_avg_compare ?? 0
                        : kpisData?.savings_mom_compare ?? 0
                ),
                icon: "solar:hand-money-bold-duotone",
                textColor: "#118d57",
                spanColor: theme.palette.mode === "dark" ? "#6dad8d" : "transparent",
                color: ColorSchema.SUCCESS,
                series:
                    (kpisData?.savings_series_data ?? [])
                        .map((item: any) => ({
                            y:
                                filterStore?.savingsFormulaFilter === "m1"
                                    ? item?.savings_avg ?? 0
                                    : item?.savings_mom ?? 0,
                            x: fDate(item?.date_interval, "yyyy-dd-MM"),
                        }))
                        .sort(
                            (a: any, b: any) =>
                                new Date(a.x).getTime() - new Date(b.x).getTime()
                        ) ?? [],
            },
            {
                title: t("dashboard.brandTraffic.totalBrandTraffic"),
                value: kpisData?.brandTraffic ?? 0,
                compareData: calculateCompareData(
                    kpisData?.brandTraffic,
                    kpisData?.brandTrafficCompare
                ),
                color: ColorSchema.SECONDARY,
                textColor: "#880e4f",
                icon: "solar:user-circle-bold-duotone",
                spanColor: theme.palette.mode === "dark" ? "#a64b7b" : "transparent",
                series:
                    kpisData?.banking_series_data
                        ?.map((item: any) => ({
                            y: item?.brandTraffic ?? 0,
                            x: fDate(item?.banking_date, "yyyy-dd-MM"),
                        }))
                        .sort(
                            (a: any, b: any) =>
                                new Date(a.x).getTime() - new Date(b.x).getTime()
                        ) ?? [],
            },
            {
                title: t("dashboard.brandTraffic.blendedCtr"),
                value: (kpisData?.blendedCtr ?? 0) * 100,
                compareData: calculateCompareData(
                    kpisData?.blendedCtr,
                    kpisData?.blendedCtrCompare
                ),
                color: ColorSchema.INFO,
                textColor: "#5119b7",
                icon: "icon-park-twotone:click",
                spanColor: theme.palette.mode === "dark" ? "#9e77da" : "transparent",
                series:
                    kpisData?.banking_series_data
                        ?.map((item: any) => ({
                            y: (item?.blendedCtr ?? 0) * 100,
                            x: fDate(item?.banking_date, "yyyy-dd-MM"),
                        }))
                        .sort(
                            (a: any, b: any) =>
                                new Date(a.x).getTime() - new Date(b.x).getTime()
                        ) ?? [],
            },
            {
                title: getRevenueOrConversionsLabel(workspace),
                value: kpisData?.revenue_or_conversions ?? 0,
                compareData: calculateCompareData(
                    kpisData?.revenue_or_conversions,
                    kpisData?.revenue_or_conversions_compare
                ),
                icon:
                    workspace?.template === "Lead"
                        ? "mdi:account-convert"
                        : "mdi:cash-multiple",
                textColor: "#006c9c",
                spanColor: theme.palette.mode === "dark" ? "#65b5c9" : "transparent",
                color: ColorSchema.TERTIARY,
                series:
                    kpisData?.banking_series_data
                        ?.map((item: any) => ({
                            y: item?.revenue_or_conversions ?? 0,
                            x: fDate(item?.banking_date, "yyyy-dd-MM"),
                        }))
                        .sort(
                            (a: any, b: any) =>
                                new Date(a.x).getTime() - new Date(b.x).getTime()
                        ) ?? [],
            },
        ];
    }, [
        kpisData,
        theme.palette.mode,
        t,
        filterStore?.savingsFormulaFilter,
        workspace?.template,
    ]);

    const { data: performanceSplitData, isFetching: isFetchingPerformanceSplit } = useGetPerformanceSplitQuery(
        {
            workspaceId: workspace?.id,
            platforms: filterStore?.platforms,
            devices: filterStore?.devices,
            countries: filterStore?.countries,
            start_date: filterStore?.start,
            end_date: filterStore?.end,
            compare_start_date: filterStore?.compareStart,
            bqTable: workspace?.bqTable,
            compare_end_date: filterStore?.compareEnd,
            savingsFormula: filterStore?.savingsFormulaFilter,
            timestep: filterStore?.timestep,
            split: "country",
            compare: filterStore?.compare,
        },
        { skip: !workspace?.id }
    );

    const skipQuery = context ? true : !filterStore.start || !filterStore.end || !workspace?.id;

    const { data: standaloneCompetitiveIntelligenceData } = useGetCompetitiveIntelligenceKpisQuery(
        {
            startDate:
                filterStore.start !== undefined
                    ? fDate(filterStore.start, "yyyy-MM-dd")
                    : null,
            endDate:
                filterStore.end !== undefined
                    ? fDate(filterStore.end, "yyyy-MM-dd")
                    : null,
            compareStartDate:
                filterStore.compareStart !== undefined
                    ? fDate(filterStore.compareStart, "yyyy-MM-dd")
                    : null,
            compareEndDate:
                filterStore.compareEnd !== undefined
                    ? fDate(filterStore.compareEnd, "yyyy-MM-dd")
                    : null,
            timestep: filterStore.timestep,
            devices: filterStore.devices,
            countries: filterStore.countries,
            keywords: filterStore.keywords,
            platforms: filterStore?.platforms,
            workspaceId: workspace?.id,
        },
        { skip: skipQuery }
    );

    const competitiveIntelligenceData = context?.competitiveIntelligenceData || (Array.isArray(standaloneCompetitiveIntelligenceData) ? standaloneCompetitiveIntelligenceData : []);

    // 🔹 optimizationOverview stabilisé
    const optimizationOverview = useMemo(() => {
        if (!performanceSplitData?.performanceSplit) return [];
        return performanceSplitData.performanceSplit.map(
            (item1: any, index: number) => {
                const match = competitiveIntelligenceData?.find(
                    (item2: any) => item2.country_name === item1.split
                );

                const topCompetitors = Array.isArray(match?.top_5_competitors)
                    ? match.top_5_competitors
                        .map((c: any) => c?.domain_name)
                        .filter(Boolean)
                    : [];

                const topCompetitorsPaidRates: Record<string, number> = Array.isArray(
                    match?.top_5_competitors
                )
                    ? match.top_5_competitors.reduce(
                        (acc: Record<string, number>, c: any) => {
                            const domain = c?.domain_name;
                            const rate = c?.paid_presence_rate;
                            if (domain && typeof rate === "number") acc[domain] = rate;
                            return acc;
                        },
                        {}
                    )
                    : {};

                return {
                    id: item1?.split ?? index,
                    ...item1,
                    ...match,
                    competitiveLandscape: match?.level ? normalizeLevel(match.level) : null,
                    levelRaw: match?.level,
                    brandDomain: workspace?.url,
                    firstCompetitorDomain:
                        Array.isArray(match?.top_5_competitors) &&
                            match.top_5_competitors[0]?.domain_name
                            ? match.top_5_competitors[0].domain_name
                            : undefined,
                    isDemoBrand: workspace?.name?.includes("Demo"),
                    topCompetitors,
                    topCompetitorsPaidRates,
                    blendedPresenceRate: match?.blended_presence_rate ?? null,
                    paidPresenceRate: match?.client_paid_presence_rate ?? null,
                    firstCompetitorPaidPresenceRate:
                        match?.first_compet_paid_presence_rate ?? null,
                    presenceRateData: {
                        organicOnly: match?.organic_only ?? 0,
                        soloBidder: match?.solo_bidder ?? 0,
                        topAdPosition: match?.top_ad_position ?? 0,
                    },
                };
            }
        );
    }, [performanceSplitData, competitiveIntelligenceData, workspace?.url, workspace?.name]);

    const { data: filtersData } = useGetFiltersQuery(
        {
            workspaceId: workspace?.id,
            start_date: filterStore?.start,
            end_date: filterStore?.end,
            countries: filterStore?.countries,
            devices: filterStore?.devices,
            platforms: filterStore?.platforms,
        },
        { skip: !workspace?.id }
    );

    useEffect(() => {
        if (filtersData && optimizationOverview.length) {
            dispatch(setFilterLoadedCountry(filtersData.countries || []));
        }
        if (filtersData) {
            if (filtersData.min_date) {
                const d = new Date(filtersData.min_date);
                d.setFullYear(2023);
                dispatch(setFilterMinDatePicker(d.toUTCString()));
            }
        }

    }, [filtersData, optimizationOverview, dispatch]);

    useEffect(() => {
        if (workspace?.name.includes('Demo')) {
            const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

            const newStartDate = new Date(2024, 8, 1); // 1er septembre 2024
            const newEndDate = new Date(2024, 10, 30); // 30 novembre 2024

            const { start, end } = getDateRangeIso(newStartDate, newEndDate, userTimeZone);

            dispatch(setFilterDate({ start, end }));
            dispatch(setFilterCompare(false))
        }
    }, [workspace, location?.pathname])

    return (
        <Box>
            {/* 🔹 Widgets bancaires */}
            <BankingStatsGrid
                stats={bankinStats}
                isFetching={isFetchingKpis}
                kpisData={kpisData}
                kpisError={kpisError}
            />

            <Box pt={2}>
                {/* 🔹 Onglet Countries */}
                <Paper
                    sx={{
                        border: theme.palette.mode === "dark"
                            ? "1px solid rgba(255, 255, 255, 0.12)"
                            : "1px solid rgba(145, 158, 171, 0.2)",
                    }}
                >

                    {isFetchingPerformanceSplit || !performanceSplitData ? (
                        <Skeleton
                            variant="rectangular"
                            height={400}
                            width="100%"
                            animation="wave"
                        />
                    ) : (
                        performanceSplitData?.performanceSplit?.some(
                            (item: any) => item.split
                        ) && (
                            <DataGridPerformanceTableBrandOverview
                                data={optimizationOverview}
                                compare={filterStore?.compare}
                            />
                        )
                    )}
                </Paper>

            </Box>
        </Box>
    );
}